import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import Cookies from 'js-cookie';
import { Button } from 'antd';
import { Icon, FloatingButton, PriceButton, ErrorPage, modal } from '@/components';
import Message from '@/components/Message';
import { ProLayout } from '@ant-design/pro-components';
import WebTracking from '@fle-sdk/event-tracking-web';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import defaultSettings from '@/const/defaultSettings';
import { AccountStore, CommonStore, PageCacheStore } from '@/stores';
import HeaderMenu, { HeaderMenuRef } from './HeaderMenu';
import SiderMenu, { SiderMenuRef } from './SiderMenu';
import Breadcrumb from './Breadcrumb';
import AuthContainer from './AuthContainer';
import HeaderLeftContent from './HeaderLeftContent';
import AvatarPopover from './AvatarPopover';
import AdvertisingModal, { type AdvertisingModalRef } from './AdvertisingModal';
import { getFlatMenus, formartMenu } from '@/tools/getFlatMenus';
import { ApplicationInfo } from '@/const/application';
import getMatchMenu from '@/tools/getMatchMenu';
import { handleHasPreviousLevel, handleSelfJdHasPreviousLevel } from '@/tools/compatibility';

import { trackMap, type TrackingMapItem } from '@/const/trackingMap';
import { pathToRegexp } from 'path-to-regexp';
import { BaseURL, ApplicationUrlMap, fleAppEnv } from '@/const/serviceEnv';

import type { UserInfo } from '@/stores/account';
import type { MenuDataItem } from '@/const/route.config';

import './index.less';
import { setTheme } from '@/tools/theme';

export interface AuthorizedModuleType {
  authorizedCount: number;
  moduleId: string | number;
  moduleName: string;
  status: 0 | 1;
  totalGrantNum: number;
}

const HasToken = Cookies.get('token');
// 纯净模式展示的路由
const pureRoute = new Map([
  ['/noAuth', true],
  ['/404', true],
  ['/redirectPage', true],
]);

// 始终有操作权限的路由
const AlwaysRightsRouteList = new Map([
  ['/module/workbench/dashboard', true],
  ['/module/workbench/account/certificationSimple', true],
  ['/webview/lgj/settlement/account/registerRecharge', true],
]);

const { systemId, title, dashboard } = ApplicationInfo;

/**
 * 根据路径匹配 tracking map 中的路由模式
 * 支持动态路由参数匹配，如 /module/lgjProduct/home/13 匹配 /module/lgjProduct/home/:homeId
 */
const getTrackingItem = (pathname: string): TrackingMapItem | undefined => {
  // 先尝试精确匹配
  const exactMatch = trackMap.get(pathname);
  if (exactMatch) {
    return exactMatch;
  }

  // 遍历所有 tracking map 的键，查找匹配的模式
  for (const [pattern] of trackMap) {
    try {
      // 使用 pathToRegexp 进行模式匹配
      const regexp = pathToRegexp(pattern);
      if (regexp.test(pathname)) {
        return trackMap.get(pattern);
      }
    } catch (error) {
      // 如果模式无效，跳过
      continue;
    }
  }

  return undefined;
};

const BasicLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isExperienceMember, hasTenThousandEquity } = AccountStore;
  const HeaderMenuRef = useRef<HeaderMenuRef>(null);
  const SiderMenuRef = useRef<SiderMenuRef>(null);
  const [menu, setMenu] = useState<MenuDataItem[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [authModule, setAuthModule] = useState<AuthorizedModuleType[]>(); // 权限模块
  const [teamSelfInfo, setTeamSelfInfo] = useState<Record<string, any>>();
  const AdvertisingModalRef = useRef<AdvertisingModalRef>(null);

  // 扁平化菜单，用于搜索，避免频繁计算
  const [flatMenus, setFlatMenus] = useState<Record<string, MenuDataItem> | null>(null);
  const [selectionRouter, setSelectionRouter] = useState<string>('');

  const handleHasToken = async () => {
    await AccountStore.getLiguanjiaToken();

    const { member_token } = await AccountStore.getLiguanjiaToken();
    const { has_previous_level } = await AccountStore.getTeamHistory(member_token);
    const teamSelfInfo = await AccountStore.getTeamSelfInfo(member_token);
    setTeamSelfInfo(teamSelfInfo);
    CommonStore.updateState({
      memberToken: member_token,
    });

    // 打开弹窗广告
    AdvertisingModalRef.current?.open();

    AccountStore.getMenuPermission({
      systemId,
      resourceTypeList: [1, 2],
    }).then(async (res) => {
      const __menu = formartMenu(res, [title]);

      // 兼容3.0旧版菜单
      handleHasPreviousLevel(__menu, has_previous_level);
      handleSelfJdHasPreviousLevel(__menu, +teamSelfInfo.is_open_self_jd === 1);

      setMenu(__menu);

      const __flatMenus = __menu.length ? getFlatMenus(__menu) : null;
      setFlatMenus(__flatMenus);
      // 因为选品首页菜单是动态的，所以需要在这里设置选品首页菜单的path给到子组件
      for (const key in __flatMenus) {
        if (key.includes('/module/lgjProduct/home/')) {
          setSelectionRouter(key);
        }
      }
      // 获取开通的权限列表，放在这里是为了避免菜单dom还没有渲染，无法获取到dom，导致无法正确设置引导
      AccountStore.getOpenRightList();
    });
    AccountStore.getUserInfo().then((data) => {
      const fenxiaoToken = Cookies.get('fenxiaoToken');
      !fenxiaoToken && AccountStore.getMiaohuoToken({ id: data?.id });

      CommonStore.getCsShowInfo(data?.id); // 咨询飞象客服
      CommonStore.getAuthModule({ employeeId: data?.id }).then((data) => setAuthModule(data));

      // 登录WebTracking
      WebTracking.login({
        useId: data.id,
        companyId: data.companyId,
      });
      setUserInfo(data);
    });
    // 获取站点id
    AccountStore.getSiteId();
  };
  useLayoutEffect(() => {
    setTheme();
    if (HasToken) {
      handleHasToken();

      // 初始化WebTracking
      WebTracking.init({
        appKey: systemId, // 万鲤商联平台系统ID
        showLog: false,
        isTrackSinglePage: true,
        sendMethod: 'xhr',
        serverUrl: `${BaseURL}/buriedpoint/v2/record/push`,
      });
    } else {
      // const loginUrl = `/account/login?redirectUrl=${encodeURIComponent(window.location.href)}`;
      const loginUrl = `${ApplicationUrlMap['88buys']?.[fleAppEnv]}/login`;
      if (window.location.hostname !== 'localhost') {
        window.location.href = loginUrl;
      }
    }
  }, []);

  const [showPageTab, setShowPageTab] = useState<boolean>(false);
  const [matchMenu, setMatchMenu] = useState<MenuDataItem[]>([]);
  const [currentMenu, setCurrentMenu] = useState<MenuDataItem>();
  useEffect(() => {
    // 切换页面时，重置滚动条
    window.scrollTo(0, 0);

    // 页面访问埋点
    const pathname = location.pathname.split('?')[0];
    const trackingItem = getTrackingItem(pathname);
    if (trackingItem) {
      WebTracking.track({
        desc: trackingItem.desc,
        pageKey: trackingItem.pageKey,
        partKey: trackingItem.partKey,
      });
    }

    if (flatMenus && location.pathname && !pureRoute.has(location.pathname)) {
      const pathname =
        location.pathname === '/redirectPage' ? location.state?.path : location.pathname;

      const _matchMenu = getMatchMenu(pathname, flatMenus, true, true);

      const checkMenu = _matchMenu?.[_matchMenu.length - 1];

      if (!checkMenu) {
        navigate('/noAuth');
        return;
      }

      CommonStore.updateState({
        currentMenu: checkMenu,
      });
      setMatchMenu(_matchMenu);
      setCurrentMenu({ ...checkMenu });

      const { layout_keys: matchMenuKeys } = checkMenu || {};
      const [key] = matchMenuKeys || [];

      let firstMenuItem: MenuDataItem | undefined;

      if (key) {
        firstMenuItem = flatMenus[key];
      }

      const { showPageTab } = JSON.parse(decodeURIComponent(firstMenuItem?.props || '{}'));
      setShowPageTab(showPageTab);

      if (checkMenu && showPageTab) {
        const singleTab = JSON.parse(decodeURIComponent(checkMenu.props || '{}'))?.singleTab;

        PageCacheStore.updatePageTab({
          key: `${location.pathname}${singleTab ? '' : location.search}`,
          name: checkMenu.name || '标签页',
          singleTab,
        });
      }
    }
  }, [flatMenus, location]);

  useEffect(() => {
    // 如果用户有10000元权益且没有看过引导且当前不在首页，则跳转到首页
    if (
      hasTenThousandEquity &&
      !localStorage.getItem(`visitTour-${userInfo?.id}`) &&
      location.pathname !== dashboard
    ) {
      navigate(dashboard);
    }

    // 如果当前在首页，则判断用户是否有权限，没有权限则弹窗提示，有权限则且没看过引导则设置引导
    if (location.pathname === dashboard) {
      if (isExperienceMember) {
        modal.confirm({
          title: '权限不足',
          content: '如需开通请前往激活',
          okText: '前往激活',
          onOk: () => {
            navigate('/module/workbench/account/certificationSimple');
          },
        });
      }

      // 设置引导
      if (hasTenThousandEquity && !localStorage.getItem(`visitTour-${userInfo?.id}`)) {
        localStorage.setItem(`visitTour-${userInfo?.id}`, '1');
        setTimeout(() => {
          HeaderMenuRef.current?.setIsTourVisible(true);
        }, 100);
      }
    }
  }, [isExperienceMember, hasTenThousandEquity, location.pathname]);

  // 判断用户是否改变
  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        AccountStore.checkUserId().then((data: any) => {
          if (!data) {
            window.location.replace('/module/workbench/dashboard');
          }
        });
      } else {
        // 当前标签页变为不可见状态
        // 执行相应的操作
      }
    });

    CommonStore.getCsPermissionInfo();
  }, []);

  const showConsoleBtn = useMemo(
    () =>
      userInfo?.adminFlag === 1 ||
      (flatMenus?.['/group/console'] && flatMenus?.['/group/console']?.children?.length),
    [userInfo, flatMenus],
  );

  return (
    <>
      <ProLayout
        {...defaultSettings}
        siderWidth={180}
        location={location}
        className="peliten__layout"
        style={{ flexDirection: 'row', height: '100vh' }}
        headerRender={(_, defaultDom) => <div id="fx__layout__header">{defaultDom}</div>}
        headerTitleRender={() => <HeaderLeftContent userInfo={userInfo} />}
        headerContentRender={() => (
          <HeaderMenu
            menuData={menu}
            matchMenuKeys={currentMenu?.layout_keys}
            ref={HeaderMenuRef}
            selectionRouter={selectionRouter}
            tourFinish={() => SiderMenuRef.current?.setIsTourVisible(true)}
          />
        )}
        actionsRender={() =>
          !!showConsoleBtn && (
            <Button
              ghost
              type="primary"
              className="fle-console-btn"
              icon={<Icon type="icon-13-xitongpeizhi" style={{ fontSize: 20 }} className="fle-icon-qiyekongzhitai" />}
              onClick={() => {
                navigate('/module/workbench/account/carpdanceAccount');
              }}
            >
              控制台
            </Button>
          )
        }
        avatarProps={{
          src: '//oss.elebuys.com/tmpdir/202308171743390002128752.png',
          title: userInfo?.name,
          style: { background: 'rgba(255,102,0,.6)' },
          render: (_, dom) => (
            <AvatarPopover userInfo={userInfo} showConsoleBtn={!!showConsoleBtn}>
              <div style={{ whiteSpace: 'nowrap' }}>
                {dom}
                <DownOutlined style={{ fontSize: 12, marginLeft: 10 }} />
              </div>
            </AvatarPopover>
          ),
        }}
        menuRender={() =>
          HasToken &&
          !matchMenu[0]?.hideChildrenInMenu && (
            <SiderMenu
              ref={SiderMenuRef}
              matchMenu={matchMenu}
              currentMenu={currentMenu}
              selectionRouter={selectionRouter}
              matchMenuKeys={currentMenu?.layout_keys}
            />
          )
        }
        footerRender={false}
      >
        <AuthContainer
          authModule={authModule}
          matchMenu={matchMenu}
          currentMenu={currentMenu}
          teamSelfInfo={teamSelfInfo}
          isExperienceMember={isExperienceMember}
        >
          <div className="fx__layout-content" style={{ paddingTop: showPageTab ? 36 : 0 }}>
            <Breadcrumb matchMenu={matchMenu} currentMenu={currentMenu} />
            {isExperienceMember && !AlwaysRightsRouteList.has(location.pathname) && (
              <div className="fle-tour-mask">
                <ErrorPage status="noPermission" />
              </div>
            )}
            <Outlet
              context={{
                userInfo,
                matchMenu,
                currentMenu,
                flatMenus,
              }}
            />
          </div>
        </AuthContainer>
      </ProLayout>

      <Message />

      {!['/showroom', '/program/mallchat'].some((path) => location.pathname.includes(path)) && (
        <FloatingButton
          userInfo={userInfo}
          openAdvertisingModal={() => AdvertisingModalRef.current?.open(1)}
        />
      )}
      <PriceButton />

      <AdvertisingModal ref={AdvertisingModalRef} />
    </>
  );
};

export default observer(BasicLayout);
